import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { POPUP_SECTIONS } from "../../constant/constant";
import { usePopupFormContext } from "../../hooks/usePopupFormContext";
import {
  isCartDrawerBundleSelectionShell,
  isCartDrawerCheckboxStackShell,
  isCartDrawerFbtBundleShell,
} from "../../../shared/product-shell-layout.js";
import { addProductPageBundleLineItems } from "../../hooks/useProductPageBundleAddToCart";
import { getProductPageSlotNo } from "../../../shared/product-page-slot.js";

const ProductPageUpsellSelectionContext = createContext(null);

function logProductPageBundleDebug(step, detail) {
  if (typeof console === "undefined") return;
  try {
    console.log(
      "[WeUpsell][ProductPageBundle][" + step + "]",
      detail !== undefined ? detail : ""
    );
  } catch (_) {
    /* ignore */
  }
}

/**
 * Multiple product-page slots (Add-Ons + FBT) can mount at once.
 * Only providers with interceptThemeAddToCart=true may own theme ATC prepend.
 * FBT must never overwrite Add-Ons on window.__WEUPSELL_PRODUCT_PAGE_BUNDLE__.
 */
function getBundleRegistry() {
  if (typeof window === "undefined") return null;
  if (!window.__WEUPSELL_PRODUCT_PAGE_BUNDLES__) {
    window.__WEUPSELL_PRODUCT_PAGE_BUNDLES__ = Object.create(null);
  }
  return window.__WEUPSELL_PRODUCT_PAGE_BUNDLES__;
}

function listInterceptingBundleApis() {
  const registry = getBundleRegistry() || {};
  return Object.keys(registry)
    .map((key) => registry[key])
    .filter((api) => api && api.interceptThemeAddToCart !== false);
}

function getOrCreateAggregateThemeBundleApi() {
  if (typeof window === "undefined") return null;
  if (window.__WEUPSELL_PRODUCT_PAGE_BUNDLE_AGGREGATE__) {
    return window.__WEUPSELL_PRODUCT_PAGE_BUNDLE_AGGREGATE__;
  }

  const aggregate = {
    get interceptThemeAddToCart() {
      return listInterceptingBundleApis().length > 0;
    },
    get themePrependExcludeFirst() {
      // Theme ATC prepend is Product Add-Ons only — never FBT lock-first.
      return false;
    },
    hasSelection(options) {
      return listInterceptingBundleApis().some((api) => {
        try {
          return api.hasSelection?.(options) === true;
        } catch (_) {
          return false;
        }
      });
    },
    getSelectedCount(options) {
      return listInterceptingBundleApis().reduce((total, api) => {
        try {
          return total + (Number(api.getSelectedCount?.(options)) || 0);
        } catch (_) {
          return total;
        }
      }, 0);
    },
    isActive(options) {
      return listInterceptingBundleApis().some((api) => {
        try {
          return api.isActive?.(options) === true;
        } catch (_) {
          return false;
        }
      });
    },
    getLineItems(options) {
      return listInterceptingBundleApis().flatMap((api) => {
        try {
          const items = api.getLineItems?.(options);
          return Array.isArray(items) ? items : [];
        } catch (_) {
          return [];
        }
      });
    },
    getProducts() {
      return listInterceptingBundleApis().flatMap((api) => {
        try {
          const products = api.getProducts?.();
          return Array.isArray(products) ? products : [];
        } catch (_) {
          return [];
        }
      });
    },
    clearSelection() {
      listInterceptingBundleApis().forEach((api) => {
        try {
          api.clearSelection?.();
        } catch (_) {
          /* ignore */
        }
      });
    },
    async addSelectedItems(options) {
      const apis = listInterceptingBundleApis().filter((api) => {
        try {
          return api.hasSelection?.(options) === true;
        } catch (_) {
          return false;
        }
      });
      let lastResult;
      for (const api of apis) {
        lastResult = await api.addSelectedItems?.(options);
      }
      return lastResult;
    },
  };

  window.__WEUPSELL_PRODUCT_PAGE_BUNDLE_AGGREGATE__ = aggregate;
  return aggregate;
}

function refreshActiveThemeInterceptBundle() {
  if (typeof window === "undefined") return;
  const registry = getBundleRegistry() || {};
  const intercepting = listInterceptingBundleApis();
  if (intercepting.length > 0) {
    window.__WEUPSELL_PRODUCT_PAGE_BUNDLE__ = getOrCreateAggregateThemeBundleApi();
  } else {
    delete window.__WEUPSELL_PRODUCT_PAGE_BUNDLE__;
  }
  logProductPageBundleDebug("registry_refresh", {
    keys: Object.keys(registry),
    interceptingCount: intercepting.length,
    activeIntercepts: Object.keys(registry).filter(
      (key) => registry[key]?.interceptThemeAddToCart !== false
    ),
  });
}

function registerThemeBundleApi(registryKey, api) {
  const registry = getBundleRegistry();
  if (!registry || !registryKey || !api) return;
  // FBT (interceptThemeAddToCart=false) must not enter the theme-ATC registry.
  if (api.interceptThemeAddToCart === false) {
    logProductPageBundleDebug("registry_skip_non_intercept", {
      registryKey,
    });
    return;
  }
  registry[registryKey] = api;
  refreshActiveThemeInterceptBundle();
}

function unregisterThemeBundleApi(registryKey, api) {
  const registry = getBundleRegistry();
  if (!registry || !registryKey) return;
  if (api == null || registry[registryKey] === api) {
    delete registry[registryKey];
  }
  refreshActiveThemeInterceptBundle();
}


function coerceShopifyVariantId(rawId) {
  if (rawId == null || rawId === "") return null;
  let value = rawId;
  if (typeof value === "string" && value.includes("gid://")) {
    value = value.split("/").pop();
  }
  // Reject synthetic picker placeholders like "123-default-variant"
  if (typeof value === "string" && /default-variant/i.test(value)) {
    return null;
  }
  if (typeof value === "string") {
    value = value.trim();
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function resolveVariantIdForProduct(productData, formValues, productIndex) {
  if (!productData) return null;

  const formKey = `${POPUP_SECTIONS.VARIANT_PICKER}-${productIndex}`;
  const variants = Array.isArray(productData.variants) ? productData.variants : [];
  const firstVariant = variants[0];
  const firstAvailable = productData.selected_or_first_available_variant;
  const firstOfferable =
    variants.find(
      (variant) =>
        variant &&
        variant.available !== false &&
        variant.availableForSale !== false
    ) || firstVariant;

  const candidates = [
    formValues?.[formKey],
    productData.variant_id,
    productData.variantId,
    firstAvailable?.id,
    firstAvailable?.legacyResourceId,
    firstAvailable?.admin_graphql_api_id,
    firstOfferable?.id,
    firstOfferable?.legacyResourceId,
    firstOfferable?.admin_graphql_api_id,
    firstVariant?.id,
    firstVariant?.legacyResourceId,
    firstVariant?.admin_graphql_api_id,
    // Single-variant / flattened offer payloads sometimes put the variant id on `id`
    !variants.length ? productData.variant_id || productData.variantId : null,
  ];

  for (const candidate of candidates) {
    const resolved = coerceShopifyVariantId(candidate);
    if (resolved != null) return resolved;
  }

  return null;
}

export function ProductPageUpsellSelectionProvider({
  children,
  popupData,
  productSection,
  products = [],
  enabled = false,
  defaultSelectFirst = false,
  lockFirstProductSelection = false,
  interceptThemeAddToCart = true,
}) {
  // Do NOT call watch() here — it re-renders on every form field change and tears
  // down/recreates window.__WEUPSELL_PRODUCT_PAGE_BUNDLE__ mid cart/add (hangs ATC).
  const { setValue, getValues } = usePopupFormContext();
  const [selectedByIndex, setSelectedByIndex] = useState({});
  const [bundleProducts, setBundleProductsState] = useState(products || []);
  const selectedByIndexRef = useRef({});
  const bundleProductsRef = useRef(products || []);
  const interceptThemeAddToCartRef = useRef(interceptThemeAddToCart);
  const lockFirstProductSelectionRef = useRef(lockFirstProductSelection);
  const popupDataRef = useRef(popupData);
  const clearProductSelectionRef = useRef(() => {});
  const getValuesRef = useRef(getValues);
  const setValueRef = useRef(setValue);
  const registryKey = useMemo(() => {
    const slotNo = getProductPageSlotNo(popupData);
    const popupId = popupData?._id || popupData?.id || "unknown";
    return `slot:${slotNo || "x"}:popup:${popupId}:intercept:${
      interceptThemeAddToCart ? "1" : "0"
    }`;
  }, [popupData, interceptThemeAddToCart]);

  useEffect(() => {
    selectedByIndexRef.current = selectedByIndex;
  }, [selectedByIndex]);

  useEffect(() => {
    bundleProductsRef.current = bundleProducts;
  }, [bundleProducts]);

  useEffect(() => {
    interceptThemeAddToCartRef.current = interceptThemeAddToCart;
  }, [interceptThemeAddToCart]);

  useEffect(() => {
    lockFirstProductSelectionRef.current = lockFirstProductSelection;
  }, [lockFirstProductSelection]);

  useEffect(() => {
    popupDataRef.current = popupData;
  }, [popupData]);

  useEffect(() => {
    getValuesRef.current = getValues;
    setValueRef.current = setValue;
  }, [getValues, setValue]);

  const setBundleProducts = useCallback((nextProducts) => {
    if (!Array.isArray(nextProducts) || nextProducts.length === 0) return;
    bundleProductsRef.current = nextProducts;
    setBundleProductsState(nextProducts);
  }, []);

  useEffect(() => {
    // Parent may pass a fresh `products={[]}` each render as a placeholder while the
    // strip syncs real catalog via setBundleProducts — never wipe a non-empty catalog.
    if (!Array.isArray(products) || products.length === 0) return;
    setBundleProducts(products);
  }, [products, setBundleProducts]);

  useEffect(() => {
    if (!enabled || !bundleProducts.length) return;
    const currentGetValues = getValuesRef.current;
    const currentSetValue = setValueRef.current;
    bundleProducts.forEach((productData, productIndex) => {
      const formKey = `${POPUP_SECTIONS.VARIANT_PICKER}-${productIndex}`;
      const existing = currentGetValues?.(formKey);
      if (existing != null && existing !== "") return;
      const variantId = resolveVariantIdForProduct(
        productData,
        currentGetValues?.() || {},
        productIndex
      );
      if (variantId == null) return;
      currentSetValue?.(formKey, String(variantId), {
        shouldDirty: false,
        shouldValidate: false,
      });
    });
  }, [bundleProducts, enabled]);

  useEffect(() => {
    if (!enabled || !defaultSelectFirst || !bundleProducts.length) return;
    setSelectedByIndex((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const next = { 0: true };
      selectedByIndexRef.current = next;
      return next;
    });
  }, [bundleProducts.length, defaultSelectFirst, enabled]);

  const resolveLineItemForIndex = useCallback((productIndex) => {
    const productsList = bundleProductsRef.current;
    const productData = productsList[productIndex];
    if (!productData) return null;
    const currentFormValues = getValuesRef.current?.() || {};
    const variantId = resolveVariantIdForProduct(
      productData,
      currentFormValues,
      productIndex
    );
    if (variantId == null) return null;
    const quantity =
      currentFormValues[`${POPUP_SECTIONS.QUANTITY_PICKER}-${productIndex}`] ?? 1;
    return {
      id: variantId,
      quantity: Number(quantity) > 0 ? Number(quantity) : 1,
      productIndex,
    };
  }, []);

  const toggleProductSelection = useCallback(
    (productIndex, nextSelected) => {
      if (lockFirstProductSelection && productIndex === 0 && nextSelected === false) {
        return;
      }
      setSelectedByIndex((prev) => {
        const isSelected = prev[productIndex] === true;
        const shouldSelect =
          typeof nextSelected === "boolean" ? nextSelected : !isSelected;
        if (!shouldSelect) {
          if (lockFirstProductSelection && productIndex === 0) return prev;
          if (!isSelected) return prev;
          const next = { ...prev };
          delete next[productIndex];
          selectedByIndexRef.current = next;
        logProductPageBundleDebug("checkbox_deselected", {
            productIndex: productIndex,
            selected: { ...next },
          });
          refreshActiveThemeInterceptBundle();
          return next;
        }
        const next = { ...prev, [productIndex]: true };
        selectedByIndexRef.current = next;
        logProductPageBundleDebug("checkbox_selected", {
          productIndex: productIndex,
          selected: { ...next },
        });
        const productData = bundleProductsRef.current[productIndex];
        const formKey = `${POPUP_SECTIONS.VARIANT_PICKER}-${productIndex}`;
        const existing = getValuesRef.current?.(formKey);
        if ((existing == null || existing === "") && productData) {
          const variantId = resolveVariantIdForProduct(
            productData,
            getValuesRef.current?.() || {},
            productIndex
          );
          if (variantId != null) {
            setValueRef.current?.(formKey, String(variantId), {
              shouldDirty: false,
              shouldValidate: false,
            });
          }
        }
        refreshActiveThemeInterceptBundle();
        return next;
      });
    },
    [lockFirstProductSelection]
  );

  const isProductSelected = useCallback(
    (productIndex) => selectedByIndex[productIndex] === true,
    [selectedByIndex]
  );

  const clearProductSelection = useCallback(() => {
    const next =
      defaultSelectFirst && lockFirstProductSelection ? { 0: true } : {};
    selectedByIndexRef.current = next;
    setSelectedByIndex(next);
  }, [defaultSelectFirst, lockFirstProductSelection]);

  clearProductSelectionRef.current = clearProductSelection;

  const getSelectedLineItems = useCallback((options = {}) => {
    const currentSelection = selectedByIndexRef.current;
    const excludeLockedFirst =
      options?.forThemePrepend === true &&
      lockFirstProductSelectionRef.current === true;
    const items = [];
    Object.keys(currentSelection).forEach((key) => {
      if (currentSelection[key] !== true) return;
      const productIndex = Number(key);
      // FBT locks index 0 as the PDP product — theme ATC already adds it.
      if (excludeLockedFirst && productIndex === 0) return;
      const lineItem = resolveLineItemForIndex(productIndex);
      if (!lineItem) return;
      items.push(lineItem);
    });
    return items;
  }, [resolveLineItemForIndex]);

  // Stable window API — only recreate when enabled flips, never on form tick.
  // Uses refs so hybrid intercept always reads the latest selection/products.
  // FBT (interceptThemeAddToCart=false) must NOT overwrite Product Add-Ons.
  useEffect(() => {
    if (!enabled) {
      unregisterThemeBundleApi(registryKey);
      return undefined;
    }

    const readLineItems = (options) => {
      const currentSelection = selectedByIndexRef.current;
      const productsList = bundleProductsRef.current;
      const currentFormValues = getValuesRef.current?.() || {};
      const excludeLockedFirst =
        options?.forThemePrepend === true &&
        lockFirstProductSelectionRef.current === true;
      const items = [];
      Object.keys(currentSelection).forEach((key) => {
        if (currentSelection[key] !== true) return;
        const productIndex = Number(key);
        if (excludeLockedFirst && productIndex === 0) return;
        const productData = productsList[productIndex];
        if (!productData) return;
        const variantId = resolveVariantIdForProduct(
          productData,
          currentFormValues,
          productIndex
        );
        if (variantId == null) return;
        const quantity =
          currentFormValues[`${POPUP_SECTIONS.QUANTITY_PICKER}-${productIndex}`] ??
          1;
        items.push({
          id: variantId,
          quantity: Number(quantity) > 0 ? Number(quantity) : 1,
          productIndex,
        });
      });
      return items;
    };

    const waitFrames = (count = 1) =>
      new Promise((resolve) => {
        let left = Math.max(1, count);
        const tick = () => {
          left -= 1;
          if (left <= 0) {
            resolve();
            return;
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });

    const api = {
      registryKey,
      interceptThemeAddToCart: true,
      themePrependExcludeFirst: false,
      hasSelection: (options) => {
        if (options?.forThemePrepend === true && lockFirstProductSelectionRef.current) {
          return Object.entries(selectedByIndexRef.current).some(
            ([key, value]) => value === true && Number(key) !== 0
          );
        }
        return Object.values(selectedByIndexRef.current).some((value) => value === true);
      },
      getSelectedCount: (options) => {
        if (options?.forThemePrepend === true && lockFirstProductSelectionRef.current) {
          return Object.entries(selectedByIndexRef.current).filter(
            ([key, value]) => value === true && Number(key) !== 0
          ).length;
        }
        return Object.values(selectedByIndexRef.current).filter((value) => value === true)
          .length;
      },
      isActive: (options) => {
        const items = readLineItems(options);
        return Array.isArray(items) && items.length > 0;
      },
      getLineItems: (options) => readLineItems(options),
      getProducts: () => bundleProductsRef.current.slice(),
      clearSelection: () => clearProductSelectionRef.current(),
      addSelectedItems: async (options) => {
        const forThemePrepend = options?.forThemePrepend === true;
        const syncCartAfterAdd =
          options && options.syncCartAfterAdd === false ? false : true;
        const getItems = () =>
          readLineItems(forThemePrepend ? { forThemePrepend: true } : {});
        let items = getItems();
        // Variant form values / catalog sync can settle a frame after checkbox tick.
        for (let attempt = 0; attempt < 4 && !items.length; attempt += 1) {
          await waitFrames(attempt === 0 ? 1 : 2);
          items = getItems();
        }
        logProductPageBundleDebug("add_selected_items", {
          forThemePrepend: forThemePrepend,
          registryKey,
          itemCount: items.length,
          items: items,
          selected: { ...selectedByIndexRef.current },
          productCount: bundleProductsRef.current.length,
        });
        if (!Array.isArray(items) || !items.length) {
          console.warn(
            "[WeUpsell][ProductPageBundle][add_selected_items_empty]",
            {
              registryKey,
              selected: { ...selectedByIndexRef.current },
              productCount: bundleProductsRef.current.length,
              products: bundleProductsRef.current.map((product, index) => ({
                index,
                id: product?.id,
                handle: product?.handle,
                variantCount: Array.isArray(product?.variants)
                  ? product.variants.length
                  : 0,
                firstVariantId: product?.variants?.[0]?.id ?? null,
              })),
            }
          );
          return;
        }
        const result = await addProductPageBundleLineItems(
          popupDataRef.current,
          getItems,
          {
            products: bundleProductsRef.current,
            syncCartAfterAdd,
            // Nested inside hybrid cart/add patch — must skip re-entry.
            bypassCartFetchPatch: forThemePrepend || syncCartAfterAdd === false,
          }
        );
        logProductPageBundleDebug("add_selected_items_done", {
          ok: !!result,
          registryKey,
        });
        clearProductSelectionRef.current();
        refreshActiveThemeInterceptBundle();
        return result;
      },
    };

    Object.defineProperty(api, "interceptThemeAddToCart", {
      configurable: true,
      enumerable: true,
      get: () => interceptThemeAddToCartRef.current,
    });
    Object.defineProperty(api, "themePrependExcludeFirst", {
      configurable: true,
      enumerable: true,
      get: () => lockFirstProductSelectionRef.current === true,
    });

    registerThemeBundleApi(registryKey, api);
    logProductPageBundleDebug("api_mounted", {
      registryKey,
      interceptThemeAddToCart: interceptThemeAddToCartRef.current,
      productCount: bundleProductsRef.current.length,
    });
    return () => {
      unregisterThemeBundleApi(registryKey, api);
    };
  }, [enabled, registryKey]);

  const value = useMemo(
    () => ({
      enabled,
      toggleProductSelection,
      isProductSelected,
      getSelectedLineItems,
      clearProductSelection,
      setBundleProducts,
      lockFirstProductSelection: lockFirstProductSelection === true,
    }),
    [
      clearProductSelection,
      enabled,
      getSelectedLineItems,
      isProductSelected,
      lockFirstProductSelection,
      setBundleProducts,
      toggleProductSelection,
    ]
  );

  return (
    <ProductPageUpsellSelectionContext.Provider value={value}>
      {children}
    </ProductPageUpsellSelectionContext.Provider>
  );
}

export function useProductPageUpsellSelection() {
  return useContext(ProductPageUpsellSelectionContext);
}

export function isProductPageCheckboxStackLayout(productSection) {
  const shell = productSection?.setting?.layout?.shell;
  return isCartDrawerCheckboxStackShell(shell);
}

export function isProductPageFbtBundleLayout(productSection) {
  const shell = productSection?.setting?.layout?.shell;
  return isCartDrawerFbtBundleShell(shell);
}

export function isProductPageBundleSelectionLayout(productSection) {
  const shell = productSection?.setting?.layout?.shell;
  return isCartDrawerBundleSelectionShell(shell);
}

ProductPageUpsellSelectionProvider.propTypes = {
  children: PropTypes.node,
  popupData: PropTypes.object,
  productSection: PropTypes.object,
  products: PropTypes.array,
  enabled: PropTypes.bool,
  defaultSelectFirst: PropTypes.bool,
  lockFirstProductSelection: PropTypes.bool,
  interceptThemeAddToCart: PropTypes.bool,
};
